import {
  addAchievedGoalToLocalStorage,
  addActivityLevelToLocalStorage,
  addAgeToLocalStorage,
  addGenderToLocalStorage,
  addGoalWeightToLocalStorage,
  addHeightToLocalStorage,
  addMainGoalToLocalStorage,
  addNutritionGoalToLocalStorage,
  addUseMetricToLocalStorage,
  addWeightToLocalStorage,
} from "@helpers/localStorage"
import { ONBOARDING_STEPS } from "@pages/onboarding/onboardingSteps"
import { trackSelfDescribingEvent } from "@snowplow/browser-tracker"
import { redirect } from "react-router-dom"
import type { Database } from "src/types/types.gen"

type ActivityLevelEnum = Database["public"]["Enums"]["activity_level_enum"]
type AchieveResultsEnum = Database["public"]["Enums"]["achieve_results_enum"]
type PrimaryNutritionGoalEnum =
  Database["public"]["Enums"]["primary_nutrition_goal_enum"]

export default async function navigateToNextOnboardingPageAction({
  request,
}: {
  request: Request
}) {
  const formData = await request.formData()

  trackFormDataWithSnowplow({ formData, url: request.url })
  saveFormDataToLocalStorage({ formData, url: request.url })

  const next = getNext(request.url)

  const currentPage = request.url.split("/").pop()?.split("?")[0]

  if (!next) {
    return redirect("/sign-up/preparing")
  }

  const nextPathname = getNextPathName({ next, url: request.url })

  if (currentPage === "success") {
    const hash = formData.get("success") as string
    return redirect(`${nextPathname}#${hash}`)
  } else {
    return redirect(nextPathname)
  }
}

function saveFormDataToLocalStorage({
  formData,
  url,
}: {
  formData: FormData
  url: string
}) {
  const currentPage = new URL(url).pathname.split("/").pop()

  switch (currentPage) {
    case "chooseGoal": {
      const value = formData.get("gender") as string
      if (value) addGenderToLocalStorage(value === "female")
      break
    }
    case "mainGoal": {
      const value = formData.get("mainGoal") as string
      if (value) addMainGoalToLocalStorage(value)
      break
    }
    case "nutritionGoal": {
      const value = formData.get("nutritionGoal") as string
      if (value)
        addNutritionGoalToLocalStorage(value as PrimaryNutritionGoalEnum)
      break
    }
    case "height": {
      const value = formData.get("height") as string
      if (value) {
        const numericValue = parseFloat(value)
        if (!isNaN(numericValue)) {
          addHeightToLocalStorage(numericValue)
          addUseMetricToLocalStorage(true)
        }
      }
      break
    }
    case "weight": {
      const value = formData.get("weight") as string
      if (value) {
        const numericValue = parseFloat(value)
        if (!isNaN(numericValue)) addWeightToLocalStorage(numericValue)
      }
      break
    }
    case "goalWeight": {
      const value = formData.get("goalWeight") as string
      if (value) {
        const numericValue = parseFloat(value)
        if (!isNaN(numericValue)) addGoalWeightToLocalStorage(numericValue)
      }
      break
    }
    case "age": {
      const value = formData.get("age") as string
      if (value) {
        const numericValue = parseInt(value, 10)
        if (!isNaN(numericValue)) addAgeToLocalStorage(numericValue)
      }
      break
    }
    case "activityLevel": {
      const value = formData.get("activityLevel") as string
      if (value) addActivityLevelToLocalStorage(value as ActivityLevelEnum)
      break
    }
    case "speedGoal": {
      const value = formData.get("speedGoal") as string
      if (value) addAchievedGoalToLocalStorage(value as AchieveResultsEnum)
      break
    }
  }
}

function getCurrentIndex(url: string): number | undefined {
  const pathname = new URL(url).pathname
  const current = pathname.split("/").pop()

  if (!current) return

  return ONBOARDING_STEPS.indexOf(current as (typeof ONBOARDING_STEPS)[number])
}

function getNext(url: string): string | undefined {
  const currentIndex = getCurrentIndex(url)

  if (currentIndex === undefined) return

  return ONBOARDING_STEPS[currentIndex + 1]
}

function getNextPathName({ next, url }: { next: string; url: string }): string {
  const currentPathname = new URL(url).pathname
  const paths = currentPathname.split("/")
  const basePathname = paths.slice(0, -1).join("/")
  const nextPathname = basePathname + "/" + next
  return nextPathname
}

function trackFormDataWithSnowplow({
  formData,
  url,
}: {
  formData: FormData
  url: string
}) {
  const entries = Array.from(formData.entries())
  const keys = [...new Set(entries.map(([key]) => key))]

  const steps_total = ONBOARDING_STEPS.length
  const currentIndex = getCurrentIndex(url)
  const step = currentIndex === undefined ? undefined : currentIndex + 1

  for (const key of keys) {
    const data = {
      answers: formData.getAll(key),
      question: key,
      step,
      steps_total,
      url,
    }

    trackSelfDescribingEvent({
      event: {
        data,
        schema: "iglu:com.asanarebel/onboarding_event/jsonschema/1-0-0",
      },
    })
  }
}
